import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Article as ArticleIcon
} from '@mui/icons-material';
import { getFirestore, collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions as preConfiguredFunctions } from '../../firebase/firebase';
import ReactMarkdown from 'react-markdown';
import './ArticleManagement.css';

const ArticleManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [articleType, setArticleType] = useState('crystal');
  const [language, setLanguage] = useState('chinese');
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchArticles = async (reset = true) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('开始获取文章列表...');
      const db = getFirestore();
      let articlesQuery;
      
      if (reset) {
        articlesQuery = query(
          collection(db, 'seoArticles'),
          orderBy('createdAt', 'desc'),
          limit(rowsPerPage)
        );
        setPage(0);
      } else {
        if (!lastVisible) {
          setHasMore(false);
          setLoading(false);
          return;
        }
        
        articlesQuery = query(
          collection(db, 'seoArticles'),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(rowsPerPage)
        );
      }
      
      const snapshot = await getDocs(articlesQuery);
      
      if (snapshot.empty) {
        setHasMore(false);
        if (reset) {
          setArticles([]);
        }
        setLoading(false);
        return;
      }
      
      // Set the last document for pagination
      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      
      // Convert documents to array of article objects
      const fetchedArticles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to JS Date
        createdAt: doc.data().createdAt?.toDate?.() || null,
        updatedAt: doc.data().updatedAt?.toDate?.() || null
      }));
      
      if (reset) {
        setArticles(fetchedArticles);
      } else {
        setArticles(prev => [...prev, ...fetchedArticles]);
      }
      
      setHasMore(snapshot.docs.length === rowsPerPage);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('文章加载失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [rowsPerPage]);

  const handleGenerateArticle = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      
      // 直接获取函数实例并明确设置区域
      const functions = getFunctions(undefined, 'asia-southeast1');
      console.log('尝试调用云函数 (asia-southeast1 区域)');
      
      try {
        // 使用httpsCallable创建函数调用
        const generateArticle = httpsCallable(functions, 'manualGenerateSEOArticle');
        
        // 调用函数并等待结果
        console.log('开始调用生成文章函数...');
        const result = await generateArticle({ 
          articleType: articleType,
          language: language
        });
        console.log('云函数调用成功');
        
        setSnackbar({
          open: true,
          message: `成功生成${language === 'chinese' ? '中文' : '英文'}${articleType === 'crystal' ? '水晶能量' : articleType === 'fortune' ? '命理知识' : '易经八卦'}文章！`,
          severity: 'success'
        });
        
        // 重新加载文章列表
        fetchArticles();
        setGenerateDialogOpen(false);
      } catch (callError) {
        // 处理调用错误
        console.error('函数调用失败:', callError);
        
        setError(`文章生成失败: ${callError.message || '未知错误'}`);
      }
    } catch (err) {
      // 处理整体错误
      console.error('函数处理错误:', err);
      setError('文章生成处理错误: ' + (err.message || '未知错误'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!selectedArticle) return;
    
    try {
      setLoading(true);
      const db = getFirestore();
      await deleteDoc(doc(db, 'seoArticles', selectedArticle.id));
      
      setSnackbar({
        open: true,
        message: '文章已成功删除',
        severity: 'success'
      });
      
      // Remove the deleted article from the state
      setArticles(articles.filter(article => article.id !== selectedArticle.id));
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error('Error deleting article:', err);
      setError('删除文章失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      // Load more data when going to next page
      fetchArticles(false);
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    fetchArticles();
  };

  const formatDate = (date) => {
    if (!date) return '无日期';
    return new Date(date).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div">
            SEO文章管理
          </Typography>
          <Box>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setGenerateDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              生成新文章
            </Button>
            <IconButton onClick={() => fetchArticles()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>标题</TableCell>
                <TableCell>类型</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell>更新时间</TableCell>
                <TableCell>浏览量</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : articles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    暂无文章数据
                  </TableCell>
                </TableRow>
              ) : (
                articles
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((article) => (
                    <TableRow key={article.id}>
                      <TableCell>{article.title}</TableCell>
                      <TableCell>
                        {article.type === 'crystal' ? '水晶能量' : 
                         article.type === 'fortune' ? '命理运势' : 
                         article.type === 'yijing' ? '易经八卦' : article.type}
                        {article.language && ` (${article.language === 'chinese' ? '中文' : '英文'})`}
                      </TableCell>
                      <TableCell>{formatDate(article.createdAt)}</TableCell>
                      <TableCell>{formatDate(article.updatedAt)}</TableCell>
                      <TableCell>{article.viewCount || 0}</TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedArticle(article);
                            setViewDialogOpen(true);
                          }}
                          title="查看文章"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedArticle(article);
                            setDeleteDialogOpen(true);
                          }}
                          title="删除文章"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={-1} // We don't know the total count with Firestore pagination
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ page }) => `第 ${page + 1} 页`}
          nextIconButtonProps={{
            disabled: !hasMore && page > 0
          }}
        />
      </Paper>
      
      {/* 生成文章对话框 */}
      <Dialog open={generateDialogOpen} onClose={() => !isGenerating && setGenerateDialogOpen(false)}>
        <DialogTitle>生成新SEO文章</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            系统将通过AI自动生成SEO优化的文章，基于产品数据库中的信息。选择文章类型和语言，然后点击生成按钮开始。
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="article-type-label">文章类型</InputLabel>
                <Select
                  labelId="article-type-label"
                  value={articleType}
                  label="文章类型"
                  onChange={(e) => setArticleType(e.target.value)}
                  disabled={isGenerating}
                >
                  <MenuItem value="crystal">水晶能量文章</MenuItem>
                  <MenuItem value="fortune">命理运势文章</MenuItem>
                  <MenuItem value="yijing">易经八卦文章</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="language-label">文章语言</InputLabel>
                <Select
                  labelId="language-label"
                  value={language}
                  label="文章语言"
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={isGenerating}
                >
                  <MenuItem value="chinese">中文</MenuItem>
                  <MenuItem value="english">英文</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {isGenerating && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>文章生成中，这可能需要几分钟...</Typography>
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateDialogOpen(false)} 
            disabled={isGenerating}
          >
            取消
          </Button>
          <Button 
            onClick={handleGenerateArticle} 
            variant="contained" 
            disabled={isGenerating}
            startIcon={isGenerating ? <CircularProgress size={20} /> : <ArticleIcon />}
          >
            {isGenerating ? '生成中...' : '生成文章'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 查看文章对话框 */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedArticle?.title}
          <Typography variant="subtitle2" color="text.secondary">
            {formatDate(selectedArticle?.createdAt)} | 
            类型: {selectedArticle?.type === 'crystal' ? '水晶能量' : 
                  selectedArticle?.type === 'fortune' ? '命理运势' : 
                  selectedArticle?.type === 'yijing' ? '易经八卦' : 
                  selectedArticle?.type} | 
            语言: {selectedArticle?.language === 'english' ? '英文' : '中文'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <div className="markdown-content">
            <ReactMarkdown>
              {selectedArticle?.content || ''}
            </ReactMarkdown>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>关闭</Button>
          <Button 
            component={Link} 
            href={`/blog/${selectedArticle?.id}`} 
            target="_blank"
            variant="outlined"
          >
            在网站查看
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 删除确认对话框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除文章 "{selectedArticle?.title}" 吗？此操作无法撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button onClick={handleDeleteArticle} color="error">
            删除
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 提示消息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticleManagement;